import { Fragment, useEffect, useRef, useState } from "react";
import { Breadcrumbs, H5, Popovers, ToolTip } from "../../AbstractElements";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import { useLoader } from "../../_helper/LoaderContext";
import { useTranslation } from "react-i18next";
import { Card, CardBody, Col, Table, Row, Form, Button, FormLabel, CardHeader, Modal, ModalHeader, ModalBody } from "react-bootstrap";
import { deletePreOrderListAPI, getPreOrderListAPI } from "./PreorderService";
import moment from "moment";
import MyPagination from "../pagination";
import { FaPlusCircle, FaPrint } from "react-icons/fa";
import { ArrowRight, Filter, PlusCircle, X } from "react-feather";
import { Input } from "reactstrap";
import { useNavigate } from "react-router";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import PrintablePreOrder from "./printPreOrder";
import { useReactToPrint } from "react-to-print";
import jsPDF from "jspdf";
import { getPresPreSignedUrl } from "../PharmacySales/SalesService";

const PreOrderList = () => {
    const { pharmacyPOS } = usePharmacy();
    const { setLoading } = useLoader();
    const { t } = useTranslation();
    const [selectedPreOrder, setSelectedPreOrder] = useState(null);
    const [downloadingOrderId, setDownloadingOrderId] = useState(null);

    const printRef = useRef();
    const [shareOrder, setShareOrder] = useState(null);
    const pdfRef = useRef();

    const [page, setPage] = useState(1);
    const [size, setSize] = useState(10);
    const [searchValue, setSearchValue] = useState("");
    const [preOrders, setPreOrders] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPreOrders, setTotalPreOrders] = useState(0);
    const [isSearching, setIsSearching] = useState(false);
    const [filterTooltipOpen, setFilterTooltipOpen] = useState(false);
    const [clearTooltipOpen, setClearTooltipOpen] = useState(false);
    const [viewTooltipOpen, setViewTooltipOpen] = useState(false);
    const [convertTooltipOpen, setConvertTooltipOpen] = useState(false);
    const [deleteTooltipOpen, setDeleteTooltipOpen] = useState(false);
    const [printTooltipOpen, setPrintTooltipOpen] = useState(false);
    const [shareTooltipOpen, setShareTooltipOpen] = useState(false);
    const [popover, setPopover] = useState(false);

    const [logoUrl, setLogoUrl] = useState("");

    const fallbackSrc = `${process.env.PUBLIC_URL}/logo.png`;

    // Resolve pharmacy logo URL once so PrintablePreOrder always receives a defined logoUrl
    useEffect(() => {
        const fileName = pharmacyPOS?.logo;

        const loadLogo = async () => {
            if (!fileName) {
                setLogoUrl(fallbackSrc);
                return;
            }

            if (fileName.startsWith("/uploads/") || fileName.startsWith("http")) {
                if (fileName.startsWith("/uploads/")) {
                    setLogoUrl(`${process.env.REACT_APP_IMAGE}${fileName}`);
                } else {
                    setLogoUrl(fileName);
                }
                return;
            }

            try {
                const response = await getPresPreSignedUrl({ key: fileName });
                setLogoUrl(response.data.data.presignedURL);
            } catch (error) {
                console.error("Error fetching logo url:", error);
                setLogoUrl(fallbackSrc);
            }
        };

        loadLogo();
    }, [pharmacyPOS?.logo, fallbackSrc]);

    const [purchaseTooltipOpen, setPurchaseTooltipOpen] = useState(false);
    const togglePurchaseTooltip = () => setPurchaseTooltipOpen(!purchaseTooltipOpen);

    const toggleFilterTooltip = () => setFilterTooltipOpen(!filterTooltipOpen);
    const toggleClearTooltip = () => setClearTooltipOpen(!clearTooltipOpen);
    const toggleViewTooltip = () => setViewTooltipOpen(!viewTooltipOpen);
    const toggleShareTooltip = () => setShareTooltipOpen(!shareTooltipOpen);
    const toggleConvertTooltip = () => setConvertTooltipOpen(!convertTooltipOpen);
    const toggleDeleteTooltip = () => setDeleteTooltipOpen(!deleteTooltipOpen);
    const togglePrintTooltip = () => setPrintTooltipOpen(!printTooltipOpen);

    const Toggle = () => setPopover(!popover);

    const [openOrder, setOpenOrder] = useState(null);
    const history = useNavigate();


    useEffect(() => {
        getPreOrderList(searchValue);
    }, [page, size]);

    const getPreOrderList = async (search = "") => {
        const body = {
            supplierId: '',
            search: search.trim(),
            page,
            limit: size,
        };
        try {
            setLoading(true);
            const response = await getPreOrderListAPI(body);
            const data = response.data.data;

            // Set the preorder list from the response
            setPreOrders(data.preorderList || []);
            setTotalPreOrders(data.totalpreorder || 0);
            setTotalCount(Math.ceil((data.totalpreorder || 0) / size));
        } catch (error) {
            console.error("Error fetching pre orders:", error);
            setPreOrders([]);
            setTotalPreOrders(0);
            setTotalCount(0);
        } finally {
            setLoading(false);
            setIsSearching(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1); // Reset to first page when searching
        setIsSearching(true);
        getPreOrderList(searchValue);
    };


    const formatDate = (dateString) => {
        return moment(dateString).format("DD-MM-YYYY HH:mm");
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalCount) {
            setPage(newPage);
        }
    };

    const handlePageSizeChange = (e) => {
        setSize(parseInt(e.target.value));
        setPage(1);
    };

    const handleClear = () => {
        setSearchValue("");
        setPage(1);
        getPreOrderList("");
    };


    const gotoAddPreOrder = () => {
        history(`${process.env.PUBLIC_URL}/add-pre-order`);
        // navigate(`${process.env.PUBLIC_URL}/purchase`, {
        //     state: { supplier }
        // });
    }

    const handleDeletePreOrder = async (preOrderId) => {
        const confirm = await Swal.fire({
            title: t("Are you sure?"),
            text: t("You want to delete this order?"),
            icon: "warning",
            showCancelButton: true,
            customClass: {
                confirmButton: 'btn-primary',
                cancelButton: 'btn-danger',
            },
            confirmButtonText: t("Yes, Delete It!"),
        });

        if (confirm.isConfirmed) {
            try {
                setLoading(true);
                const response = await deletePreOrderListAPI({
                    clinicId: pharmacyPOS?.hospital_id,
                    preorderId: preOrderId,
                });
                if (response.data.status === "success") {
                    toast.success(response.data.message || t("Pre-order deleted successfully"));
                    getPreOrderList();
                }
            } catch (error) {
                console.error("Error deleting supplier:", error);
            } finally {
                setLoading(false);
            }
        }
    }

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        documentTitle: selectedPreOrder
            ? `PreOrder_${selectedPreOrder.orderNumber || selectedPreOrder._id}`
            : "PreOrder",
        pageStyle: `
        @page { size: auto; margin: 10mm; }
        body { font-family: Arial; }
    `,
        onAfterPrint: () => setSelectedPreOrder(null),
    });

    const generateAndSharePDF = async (order) => {
        setDownloadingOrderId(order._id);
        setShareOrder(order);
        setTimeout(async () => {
            const element = pdfRef.current;

            // Set the element to be visible (but off-screen) for better rendering
            element.style.position = "fixed";
            element.style.left = "0";
            element.style.top = "0";
            element.style.width = "800px";
            element.style.zIndex = "9999";
            element.style.visibility = "visible";
            element.style.opacity = "1";

            const pdf = new jsPDF({
                orientation: "portrait",
                unit: "mm",
                format: "a4",
            });

            // Get page dimensions
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            // Calculate scale to fit content
            const elementWidth = 800; // Your HTML width
            const elementHeight = element.scrollHeight;
            const scale = Math.min(
                (pageWidth - 20) / elementWidth, // 20mm for margins
                (pageHeight - 20) / elementHeight
            );

            await pdf.html(element, {
                x: 10,
                y: 10,
                html2canvas: {
                    scale: 0.2, // Lower scale for better quality
                    useCORS: true,
                    backgroundColor: "#ffffff",
                    logging: false,
                    removeContainer: true,
                    width: elementWidth,
                    height: elementHeight,
                    windowWidth: elementWidth,
                    windowHeight: elementHeight,
                    onclone: function (clonedDoc) {
                        // Ensure all styles are applied
                        clonedDoc.body.style.fontFamily = "'Helvetica Neue', Helvetica, Arial, sans-serif";
                        clonedDoc.body.style.fontSize = "10px";
                        clonedDoc.body.style.color = "#333";
                        clonedDoc.body.style.padding = "0";
                        clonedDoc.body.style.margin = "0";
                    }
                },
                callback: (doc) => {
                    const fileName = `Order_${order.orderNumber || order._id}.pdf`;
                    doc.save(fileName);

                    // Reset element styles
                    element.style.position = "";
                    element.style.left = "";
                    element.style.top = "";
                    element.style.width = "";
                    element.style.zIndex = "";
                    element.style.visibility = "";
                    element.style.opacity = "";

                    setShareOrder(null);
                    const phone = order?.supplierInfo?.phoneNumber;
                    const message = `Hello ${order.supplierInfo.companyName},\nPlease find the attached Pre-Order PDF.`;

                    const whatsappURL = phone
                        ? `https://wa.me/${phone}?text=${encodeURIComponent(message)}`
                        : `https://wa.me/?text=${encodeURIComponent(message)}`;

                    window.open(whatsappURL, "_blank");
                    setDownloadingOrderId(null);
                },
                autoPaging: 'text',
                margin: [10, 10, 10, 10],
            });
        }, 3000); // Increased timeout for better rendering
    };


    return (
        <>
            <Fragment>
                <Breadcrumbs
                    mainTitle={t("Order List")}
                    parent={t("Dashboard")}
                    title={t("Order List")}
                />

                <Col sm="12">
                    <Card>
                        <div className="row" style={{ alignItems: "center", padding: "12px 24px" }}>
                            <Col sm="8" lg="8" md="8">
                                <Input
                                    type="text"
                                    placeholder={t("Search by brand name, salt name, manufacturer...")}
                                    value={searchValue}
                                    onChange={(e) => setSearchValue(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") handleSearch();
                                    }}
                                />
                            </Col>
                            <Col className="col-md-4 col-sm-4 d-flex justify-content-start align-items-center" style={{ gap: '18px' }}>
                                <button id='FilterToolTip' className='btn btn-primary' style={{ height: "40px" }} onClick={handleSearch}>
                                    <Filter />
                                </button>
                                <ToolTip
                                    attrToolTip={{
                                        placement: 'top',
                                        isOpen: filterTooltipOpen,
                                        target: 'FilterToolTip',
                                        toggle: toggleFilterTooltip
                                    }}
                                >
                                    <strong>{t("Filter")}</strong>
                                </ToolTip>
                                <button id='ClearToolTip' className='btn btn-secondary' style={{ height: "40px" }} onClick={handleClear}>
                                    <X />
                                </button>
                                <ToolTip
                                    attrToolTip={{
                                        placement: 'top',
                                        isOpen: clearTooltipOpen,
                                        target: 'ClearToolTip',
                                        toggle: toggleClearTooltip
                                    }}
                                >
                                    <strong>{t("Clear")}</strong>
                                </ToolTip>
                                <button
                                    className="btn btn-primary d-flex align-items-center ms-auto"
                                    style={{ color: 'white', height: "40px" }}
                                    to=""
                                    onClick={gotoAddPreOrder}>
                                    <PlusCircle className="me-2" /> {t("Add Order")}
                                </button>
                            </Col>
                        </div>
                    </Card>
                    <Card>
                        <div className="card-block">
                            <div className="row" style={{ padding: "12px 24px" }}>
                                <Col sm="12">
                                    {/* Pre Orders Table */}
                                    <div className="table-responsive rounded">
                                        <Table className='table-center table-bordered mb-0 table-hover'>
                                            <thead>
                                                <tr>
                                                    <th width="5%">#</th>
                                                    <th width="15%">{t("Supplier")}</th>
                                                    <th width="15%">{t("Created Date")}</th>
                                                    <th width="10%">{t("Items Count")}</th>
                                                    <th>{t("Items")}</th>
                                                    <th>{t("Manufacturer")}</th>
                                                    <th width="5%">{t("Share")}</th>
                                                    <th width="10%">{t("Actions")}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {preOrders.length > 0 ? (
                                                    preOrders.map((order, index) => (
                                                        <tr key={order._id}>
                                                            <td>{(page - 1) * size + index + 1}</td>
                                                            <td>
                                                                {order.supplierInfo.companyName || t("N/A")}
                                                            </td>
                                                            <td>{formatDate(order.createdAt)}</td>
                                                            <td className="text-center">
                                                                <span className="badge bg-primary">
                                                                    {order.preorderData?.length || 0}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <div className="items-list">
                                                                    {order.preorderData && order.preorderData.length > 0 ? (
                                                                        <div>
                                                                            <strong className="d-block mb-1">
                                                                                {order.preorderData[0].brandName}
                                                                            </strong>
                                                                            <small className="text-muted d-block">
                                                                                {order.preorderData[0].saltName}
                                                                            </small>
                                                                            {order.preorderData.length > 1 && (
                                                                                <span
                                                                                    className="text-dark" style={{ cursor: 'pointer' }}
                                                                                    onClick={() => setOpenOrder(order)}
                                                                                >
                                                                                    + {order.preorderData.length - 1} {t("View All")}
                                                                                </span>

                                                                            )}
                                                                        </div>
                                                                    ) : (
                                                                        <span className="text-muted">{t("No items")}</span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                {order.preorderData?.slice(0, 2).map((item, i) => (
                                                                    <div key={i} className="mb-1">
                                                                        <div>{item.manufacturer}</div>
                                                                    </div>
                                                                ))}
                                                                {order.preorderData?.length > 2 && (
                                                                    <>
                                                                        <small className="text-primary tw-cursor-pointer" id={`popover-${order._id}`} onClick={() => setPopover(!popover)}>
                                                                            +{order.preorderData.length - 2} more
                                                                        </small>
                                                                        <Popovers placement='top' isOpen={popover} target={`popover-${order._id}`} toggle={Toggle} title={t("Manufacturers")}>
                                                                            {order.preorderData?.map((item, i) => (
                                                                                <div key={i} className="mb-1">
                                                                                    <div className="bg-body-secondary rounded-2 px-2 py-1">{item.manufacturer}</div>
                                                                                </div>
                                                                            ))}
                                                                        </Popovers>
                                                                    </>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <div className="d-flex justify-content-center gap-2">
                                                                    <button
                                                                        className='success-action-button'
                                                                        id="ShareToolTip"
                                                                        type="button"
                                                                        onClick={() => generateAndSharePDF(order)}
                                                                    >
                                                                        {downloadingOrderId === order._id ? (
                                                                            <i className="fa fa-spinner " style={{ fontSize: "18px" }}></i>
                                                                        ) : (
                                                                            <i className="fa fa-share-alt" style={{ fontSize: "18px" }}></i>
                                                                        )}
                                                                        <ToolTip
                                                                            attrToolTip={{
                                                                                placement: 'top',
                                                                                isOpen: shareTooltipOpen,
                                                                                target: 'ShareToolTip',
                                                                                toggle: toggleShareTooltip,
                                                                            }} >
                                                                            <strong>{t("Share order with supplier")}</strong>
                                                                        </ToolTip>
                                                                    </button>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="d-flex gap-2">
                                                                    <button
                                                                        className="success-action-button"
                                                                        id="PrintToolTip"
                                                                        onClick={() => {
                                                                            setSelectedPreOrder(order);
                                                                            setTimeout(() => {
                                                                                handlePrint();
                                                                            }, 100);
                                                                        }}

                                                                    >
                                                                        <FaPrint style={{ fontSize: "16px" }} />
                                                                        <ToolTip
                                                                            attrToolTip={{
                                                                                placement: 'top',
                                                                                isOpen: viewTooltipOpen,
                                                                                target: 'PrintToolTip',
                                                                                toggle: toggleViewTooltip,
                                                                            }} >
                                                                            <strong>{t("Print Order")}</strong>
                                                                        </ToolTip>
                                                                    </button>
                                                                    <button className='success-action-button' id="ViewToolTip"
                                                                        onClick={() => history(`${process.env.PUBLIC_URL}/update-pre-order/${order._id}`)}>
                                                                        <i className="fa fa-pencil-square" style={{ fontSize: "18px" }}></i>
                                                                        <ToolTip
                                                                            attrToolTip={{
                                                                                placement: 'top',
                                                                                isOpen: printTooltipOpen,
                                                                                target: 'ViewToolTip',
                                                                                toggle: togglePrintTooltip,
                                                                            }} >
                                                                            <strong>{t("Update")}</strong>
                                                                        </ToolTip>
                                                                    </button>
                                                                    <button
                                                                        className='danger-action-button'
                                                                        id="DeleteToolTip"
                                                                        onClick={() => handleDeletePreOrder(order._id)}
                                                                    >
                                                                        <i className="fa fa-trash" style={{ fontSize: "18px" }}></i>
                                                                        <ToolTip
                                                                            attrToolTip={{
                                                                                placement: 'top',
                                                                                isOpen: deleteTooltipOpen,
                                                                                target: 'DeleteToolTip',
                                                                                toggle: toggleDeleteTooltip,
                                                                            }} >
                                                                            <strong>{t("Delete")}</strong>
                                                                        </ToolTip>
                                                                    </button>
                                                                    <button
                                                                        className="success-action-button"
                                                                        id="PurchaseToolTip"
                                                                        onClick={() => {
                                                                            history(`${process.env.PUBLIC_URL}/purchase-from-preorder`, {
                                                                                state: {
                                                                                    preOrderData: order
                                                                                }
                                                                            });
                                                                        }}
                                                                        title={t("Create Purchase from this Pre-Order")}
                                                                    >
                                                                        <ArrowRight style={{ fontSize: "16px" }} />
                                                                        <ToolTip
                                                                            attrToolTip={{
                                                                                placement: 'top',
                                                                                isOpen: purchaseTooltipOpen,
                                                                                target: 'PurchaseToolTip',
                                                                                toggle: togglePurchaseTooltip,
                                                                            }}
                                                                        >
                                                                            <strong>{t("Purchase")}</strong>
                                                                        </ToolTip>
                                                                    </button>
                                                                    {/* <button className='success-action-button me-1' id="ConvertToolTip" onClick={() => {
                                                                        console.log("convert order:", order._id);
                                                                    }}>
                                                                        <i className="fa fa-refresh" style={{ fontSize: "18px" }}></i>
                                                                        <ToolTip
                                                                            attrToolTip={{
                                                                                placement: 'top',
                                                                                isOpen: convertTooltipOpen,
                                                                                target: 'ConvertToolTip',
                                                                                toggle: toggleConvertTooltip,
                                                                            }} >
                                                                            <strong>{t("Convert")}</strong>
                                                                        </ToolTip>
                                                                    </button> */}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="7" className="text-center py-4">
                                                            <div className="text-muted">
                                                                <i className="fa fa-box-open fa-2x mb-2"></i>
                                                                <p className="mb-0">{t("No pre orders found")}</p>
                                                                {searchValue && (
                                                                    <small className="d-block mt-1">
                                                                        {t('Try a different search term or clear the search')}
                                                                    </small>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Col>
                                <Col sm="12" className="mt-4">
                                    {/* Pagination */}
                                    <div className="mt-3 d-md-flex align-items-center justify-content-between">
                                        <div className="show-entries">
                                            <FormLabel>{t("Show")}&nbsp;</FormLabel>
                                            <select
                                                value={size}
                                                onChange={handlePageSizeChange}
                                                className="form-control"
                                                style={{ display: "inline", width: "auto" }}
                                            >
                                                <option value={5}>5</option>
                                                <option value={10}>10</option>
                                                <option value={20}>20</option>
                                            </select>
                                            &nbsp;<FormLabel>{t("entries")}</FormLabel>
                                        </div>
                                        <MyPagination
                                            totalPages={totalCount}
                                            currentPage={page}
                                            onPageChange={handlePageChange}
                                        />
                                    </div>
                                </Col>
                            </div>
                        </div>
                    </Card>
                </Col>
            </Fragment>
            <Modal show={!!openOrder} onHide={() => setOpenOrder(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t("Order Items")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {openOrder?.preorderData.map((item, i) => (
                        <div key={i} className={`mb-2 ${i + 1 === openOrder.preorderData.length ? '' : 'border-bottom'}`}>
                            <strong>{item.brandName}</strong>
                            <div className="text-muted small">{item.saltName}</div>
                        </div>
                    ))}
                </Modal.Body>
            </Modal>

            {/* PRINT */}
            {selectedPreOrder && (
                <div style={{ display: "none" }}>
                    <PrintablePreOrder
                        ref={printRef}
                        logoUrl={logoUrl}
                        preorder={selectedPreOrder}
                        supplier={selectedPreOrder.supplierInfo}
                        items={selectedPreOrder.preorderData}
                        t={t}
                    />
                </div>
            )}

            {/* SHARE */}
            {shareOrder && (
                <div style={{ display: "none" }}>
                    <PrintablePreOrder
                        ref={pdfRef}
                        logoUrl={logoUrl}
                        preorder={shareOrder}
                        supplier={shareOrder.supplierInfo}
                        items={shareOrder.preorderData}
                        t={t}
                    />
                </div>
            )}
        </>
    );
};

export default PreOrderList;